import { useState, useEffect } from "react";
import { supabase } from "../../config/supabaseClient";
import type { Project, ProjectImage } from "../../types/database";

const PROYECTOS_POR_PAGINA = 12;

/**
 * LazyImage para URLs dinámicas de Supabase Storage
 */
const LazyImage = ({ src, alt, eager = false }: { src: string; alt: string; eager?: boolean }) => {
  return (
    <img
      src={src}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      className={
        eager
          ? "max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          : "w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
      }
      style={eager ? { maxWidth: "90vw", maxHeight: "90vh" } : undefined}
    />
  );
};

export default function ProyectosSection() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PROYECTOS_POR_PAGINA);
  const [proyectoAmpliadoIndex, setProyectoAmpliadoIndex] = useState<number | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(*)')
        .eq('active', true)
        .order('order', { ascending: true });

      if (error) throw error;
      setProjects(data as Project[]);
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const abrirProyectoAmpliado = (index: number) => {
    setProyectoAmpliadoIndex(index);
    setCurrentImageIndex(0);
    document.body.style.overflow = "hidden";
  };

  const cerrarProyectoAmpliado = () => {
    setProyectoAmpliadoIndex(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = "unset";
  };

  const cargarMasProyectos = () => {
    setVisibleCount((prev) => prev + PROYECTOS_POR_PAGINA);
  };

  const proyectosVisibles = projects.slice(0, visibleCount);
  const proyectoActual = proyectoAmpliadoIndex !== null ? projects[proyectoAmpliadoIndex] : null;

  // Navegación ENTRE PROYECTOS
  const siguienteProyecto = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (proyectoAmpliadoIndex === null) return;
    const nextIndex = (proyectoAmpliadoIndex + 1) % projects.length;
    setProyectoAmpliadoIndex(nextIndex);
    setCurrentImageIndex(0);
  };

  const anteriorProyecto = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (proyectoAmpliadoIndex === null) return;
    const prevIndex = (proyectoAmpliadoIndex - 1 + projects.length) % projects.length;
    setProyectoAmpliadoIndex(prevIndex);
    setCurrentImageIndex(0);
  };

  // Navegación de IMÁGENES dentro del mismo proyecto (si hubiera varias)
  const siguienteImagen = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!proyectoActual?.project_images) return;
    setCurrentImageIndex((prev) => (prev + 1) % (proyectoActual.project_images?.length || 1));
  };

  const anteriorImagen = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!proyectoActual?.project_images) return;
    setCurrentImageIndex((prev) => (prev - 1 + (proyectoActual.project_images?.length || 1)) % (proyectoActual.project_images?.length || 1));
  };

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-400">Cargando proyectos...</div>;
  }

  return (
    <>
      {/* Grid de Proyectos */}
      <div
        className="grid mx-auto mb-10"
        style={{
          width: "90%",
          gridTemplateColumns: "repeat(auto-fit, minmax(clamp(200px, 20vw, 280px), 1fr))",
          gap: "clamp(1rem, 2vw, 2rem)",
        }}
      >
        {proyectosVisibles.map((proyecto, index) => {
          const portada = proyecto.project_images?.find(img => img.is_cover)?.url || proyecto.project_images?.[0]?.url;
          return (
            <div key={proyecto.id} className="group cursor-pointer" onClick={() => abrirProyectoAmpliado(index)}>
              <div className="aspect-square bg-gray-100 overflow-hidden relative shadow-md hover:shadow-xl transition-shadow duration-300">
                {portada && <LazyImage src={portada} alt={proyecto.title} />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Botón Cargar Más */}
      {visibleCount < projects.length && (
        <div className="flex justify-center mt-10">
          <button
            onClick={cargarMasProyectos}
            className="bg-gray-800 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-all duration-300 shadow-md hover:shadow-xl mb-10"
          >
            Cargar más proyectos
          </button>
        </div>
      )}

      {/* Modal de Proyecto Ampliado */}
      {proyectoActual && proyectoActual.project_images && proyectoActual.project_images.length > 0 && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          style={{ padding: "clamp(1rem, 2vw, 2rem)" }}
          onClick={cerrarProyectoAmpliado}
        >
          <div
            className="relative w-full h-full flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón Cerrar */}
            <button
              onClick={cerrarProyectoAmpliado}
              className="absolute z-30 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center cursor-pointer transition-colors"
              style={{
                top: "1rem",
                right: "1rem",
                width: "3rem",
                height: "3rem",
              }}
            >
              <i className="ri-close-line" style={{ fontSize: "1.5rem" }} />
            </button>

            {/* Flechas Navegación de Proyectos (Grandes) */}
            <button
              onClick={anteriorProyecto}
              className="absolute left-4 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center cursor-pointer transition-all"
              style={{ width: "4rem", height: "4rem" }}
            >
              <i className="ri-arrow-left-s-line" style={{ fontSize: "2.5rem" }} />
            </button>
            <button
              onClick={siguienteProyecto}
              className="absolute right-4 z-20 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center cursor-pointer transition-all"
              style={{ width: "4rem", height: "4rem" }}
            >
              <i className="ri-arrow-right-s-line" style={{ fontSize: "2.5rem" }} />
            </button>

            {/* Contador de Proyectos Totales - Resaltado para mejor visibilidad */}
            <div className="absolute top-4 left-4 md:top-8 md:left-8 text-white z-40">
              <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 shadow-2xl flex items-center gap-2">
                <span className="text-[10px] md:text-xs tracking-[0.3em] uppercase font-light opacity-70">Proyecto</span>
                <span className="text-sm md:text-base font-medium">{(proyectoAmpliadoIndex || 0) + 1} <span className="opacity-40 mx-1">/</span> {projects.length}</span>
              </div>
            </div>

            {/* Flechas pequeñas para fotos del mismo proyecto (solo si hay más de una) */}
            {proyectoActual.project_images.length > 1 && (
               <div className="absolute bottom-10 right-10 flex gap-4 z-20">
                  <button onClick={anteriorImagen} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <span className="text-white text-sm self-center">Foto {currentImageIndex + 1} / {proyectoActual.project_images.length}</span>
                  <button onClick={siguienteImagen} className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full">
                    <i className="ri-arrow-right-line"></i>
                  </button>
               </div>
            )}

            {/* Imagen Ampliada */}
            <div className="relative w-full h-full flex items-center justify-center">
              <LazyImage 
                src={proyectoActual.project_images[currentImageIndex].url} 
                alt={proyectoActual.title} 
                eager 
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}


